/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { UnitPositionType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitPositionProps {
  id: string;
  name: string;
  type: UnitPositionType;
  isFlightCrew: boolean;
  isNonEmployee: boolean;
  notificationAddress: string | null;
  teamMemberId: string | null;
  firstName: string | null;
  lastName: string | null;
  unitId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class UnitPositionRecord extends Record<UnitPositionProps>({
  id: null as any,
  name: null as any,
  type: null as any,
  isFlightCrew: null as any,
  isNonEmployee: null as any,
  notificationAddress: null as any,
  teamMemberId: null as any,
  firstName: null as any,
  lastName: null as any,
  unitId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    type: UnitPositionType;
    isFlightCrew: boolean;
    isNonEmployee: boolean;
    notificationAddress: string | null;
    teamMemberId: string | null;
    firstName: string | null;
    lastName: string | null;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): UnitPositionRecord {
    if (json == null) {
      throw new Error('UnitPositionRecord.fromJS: parameter cannot be null');
    }
    return new UnitPositionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitPositionJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'UnitPositionJson.name'),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', UnitPositionType.Pilot),
        'UnitPositionJson.type',
      ),
      isFlightCrew: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isFlightCrew', false),
        'UnitPositionJson.isFlightCrew',
      ),
      isNonEmployee: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isNonEmployee', false),
        'UnitPositionJson.isNonEmployee',
      ),
      notificationAddress: json.notificationAddress,
      teamMemberId: json.teamMemberId,
      firstName: json.firstName,
      lastName: json.lastName,
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'UnitPositionJson.unitId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'UnitPositionJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    type: UnitPositionType;
    isFlightCrew: boolean;
    isNonEmployee: boolean;
    notificationAddress: string | null;
    teamMemberId: string | null;
    firstName: string | null;
    lastName: string | null;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [UnitPositionRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitPositionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitPositionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitPositionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'UnitPositionJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', UnitPositionType.Pilot),
            'UnitPositionJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isFlightCrew: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isFlightCrew', false),
            'UnitPositionJson.isFlightCrew',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isNonEmployee: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isNonEmployee', false),
            'UnitPositionJson.isNonEmployee',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationAddress: tryOrDefault(
        () => json.notificationAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      teamMemberId: tryOrDefault(
        () => json.teamMemberId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: tryOrDefault(
        () => json.firstName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: tryOrDefault(
        () => json.lastName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'UnitPositionJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'UnitPositionJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: UnitPositionProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitPositionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitPositionViewModelBase implements UnitPositionProps {
  protected readonly _props: UnitPositionProps;
  private _json?: UnitPositionProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get type(): UnitPositionType {
    return this._props.type;
  }
  get isFlightCrew(): boolean {
    return this._props.isFlightCrew;
  }
  get isNonEmployee(): boolean {
    return this._props.isNonEmployee;
  }
  get notificationAddress(): string | null {
    return this._props.notificationAddress;
  }
  get teamMemberId(): string | null {
    return this._props.teamMemberId;
  }
  get firstName(): string | null {
    return this._props.firstName;
  }
  get lastName(): string | null {
    return this._props.lastName;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isClinician() {
    return this._props.type === UnitPositionType.Clinician;
  }
  public get isClinicianTrainee() {
    return this._props.type === UnitPositionType.ClinicianTrainee;
  }
  public get isCoPilot() {
    return this._props.type === UnitPositionType.CoPilot;
  }
  public get isDriver() {
    return this._props.type === UnitPositionType.Driver;
  }
  public get isMechanic() {
    return this._props.type === UnitPositionType.Mechanic;
  }
  public get isNurse() {
    return this._props.type === UnitPositionType.Nurse;
  }
  public get isObserver() {
    return this._props.type === UnitPositionType.Observer;
  }
  public get isOther() {
    return this._props.type === UnitPositionType.Other;
  }
  public get isParamedic() {
    return this._props.type === UnitPositionType.Paramedic;
  }
  public get isPilot() {
    return this._props.type === UnitPositionType.Pilot;
  }
  public get isPilotTrainee() {
    return this._props.type === UnitPositionType.PilotTrainee;
  }

  public isAnyType(...unitPositionType: UnitPositionType[]) {
    return unitPositionType.includes(this._props.type);
  }

  constructor(props: UnitPositionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        type: this._props.type,
        isFlightCrew: this._props.isFlightCrew,
        isNonEmployee: this._props.isNonEmployee,
        notificationAddress: this._props.notificationAddress,
        teamMemberId: this._props.teamMemberId,
        firstName: this._props.firstName,
        lastName: this._props.lastName,
        unitId: this._props.unitId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllUnitPositionEntities = (state: AppState) =>
  state.entityStores.unitPosition.entities;

export const selectUnitPositionById = (id: string) => (state: AppState) =>
  state.entityStores.unitPosition.entities.get(id);

export const selectUnitPositionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.unitPosition.meta.get(id);

export const selectUnitPositionAndMetadataById = (id: string) =>
  createSelector(
    selectUnitPositionById(id),
    selectUnitPositionMetadataById(id),
    (unitPosition, meta) => ({ unitPosition, meta }),
  );

export function selectUnitPositionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllUnitPositionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, UnitPositionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectUnitPositionEntitiesByName(name: string) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByType(type: UnitPositionType) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByIsFlightCrew(isFlightCrew: boolean) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.isFlightCrew === isFlightCrew).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByIsNonEmployee(isNonEmployee: boolean) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.isNonEmployee === isNonEmployee).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByNotificationAddress(notificationAddress: string) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.notificationAddress === notificationAddress).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByTeamMemberId(teamMemberId: string) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.teamMemberId === teamMemberId).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByFirstName(firstName: string) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.firstName === firstName).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByLastName(lastName: string) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.lastName === lastName).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectUnitPositionEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllUnitPositionEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
